import React, { useState, useMemo, useEffect } from "react";
import { createPortal } from "react-dom";
import {
  X,
  Bell,
  Search,
  Calendar,
  Filter,
  CheckCheck,
  ExternalLink,
  ChevronRight,
  Inbox,
  ShieldAlert,
  ArrowLeft,
  RotateCcw,
} from "lucide-react";
import {
  AppNotification,
  markAllNotificationsAsRead,
  normalizeNotificationTimestamp,
} from "../../services/notificationService";
import { NotificationDetailModal } from "./NotificationDetailModal";

interface NotificationHistoryModalProps {
  isOpen: boolean;
  onClose: () => void;
  notifications: AppNotification[];
  cleanUid?: string;
  onNavigate?: (path: string) => void;
}

export type FilterMode =
  | "all"
  | "hoje"
  | "ontem"
  | "5dias"
  | "7dias"
  | "semana_passada"
  | "30dias"
  | "period";

type ReadFilter = "all" | "unread" | "read";

/**
 * Retorna true se a data cair na semana calendário anterior (segunda-feira a domingo) em relação a 'now'
 */
export function isDateInPreviousCalendarWeek(targetDate: Date, now: Date = new Date()): boolean {
  const current = new Date(now);
  const day = current.getDay(); // 0 is Sun, 1 is Mon, ... 6 is Sat
  const diffToMonday = day === 0 ? 6 : day - 1;

  const currentMonday = new Date(current);
  currentMonday.setDate(current.getDate() - diffToMonday);
  currentMonday.setHours(0, 0, 0, 0);

  const prevMonday = new Date(currentMonday);
  prevMonday.setDate(currentMonday.getDate() - 7);

  const prevSunday = new Date(currentMonday);
  prevSunday.setMilliseconds(-1);

  return targetDate >= prevMonday && targetDate <= prevSunday;
}

export const NotificationHistoryModal: React.FC<NotificationHistoryModalProps> = ({
  isOpen,
  onClose,
  notifications,
  cleanUid,
  onNavigate,
}) => {
  const [searchTerm, setSearchText] = useState("");
  const [filterMode, setFilterMode] = useState<FilterMode>("all");
  const [readFilter, setReadFilter] = useState<ReadFilter>("all");

  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");

  const [selectedNotification, setSelectedNotification] = useState<AppNotification | null>(null);

  // Bloqueia scroll do body da página principal enquanto o histórico estiver aberto
  useEffect(() => {
    if (!isOpen) return;
    const originalOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = originalOverflow;
    };
  }, [isOpen]);

  // Validação do intervalo personalizado: Data inicial <= Data final
  const isInvalidDateRange =
    filterMode === "period" && Boolean(startDate && endDate && startDate > endDate);

  // Lista de notificações estritamente filtrada e sempre ordenada DESC (mais recente primeiro)
  const filteredNotifications = useMemo(() => {
    const list = notifications.filter((notif) => {
      // 1. Filtro de Busca Textual (busca em título, mensagem, tipo e linkUrl)
      if (searchTerm.trim().length > 0) {
        const query = searchTerm.trim().toLowerCase();
        const titleMatch = (notif.title || "").toLowerCase().includes(query);
        const msgMatch = (notif.message || "").toLowerCase().includes(query);
        const typeMatch = (notif.type || "").toLowerCase().includes(query);
        const linkMatch = (notif.linkUrl || "").toLowerCase().includes(query);
        if (!titleMatch && !msgMatch && !typeMatch && !linkMatch) {
          return false;
        }
      }

      // 2. Filtro de Leitura
      if (readFilter === "unread" && notif.read) return false;
      if (readFilter === "read" && !notif.read) return false;

      // 3. Filtros Temporais
      const ts = normalizeNotificationTimestamp(notif.createdAt);
      if (!ts) return filterMode === "all";

      const notifDate = new Date(ts);
      if (isNaN(notifDate.getTime())) return false;

      const now = new Date();

      if (filterMode === "hoje") {
        const startToday = new Date(now.getFullYear(), now.getMonth(), now.getDate(), 0, 0, 0, 0);
        const endToday = new Date(now.getFullYear(), now.getMonth(), now.getDate(), 23, 59, 59, 999);
        if (notifDate < startToday || notifDate > endToday) return false;
      }

      if (filterMode === "ontem") {
        const startYesterday = new Date(now.getFullYear(), now.getMonth(), now.getDate() - 1, 0, 0, 0, 0);
        const endYesterday = new Date(now.getFullYear(), now.getMonth(), now.getDate() - 1, 23, 59, 59, 999);
        if (notifDate < startYesterday || notifDate > endYesterday) return false;
      }

      if (filterMode === "5dias") {
        const start5Days = new Date(now.getFullYear(), now.getMonth(), now.getDate() - 4, 0, 0, 0, 0);
        const endToday = new Date(now.getFullYear(), now.getMonth(), now.getDate(), 23, 59, 59, 999);
        if (notifDate < start5Days || notifDate > endToday) return false;
      }

      if (filterMode === "7dias") {
        const start7Days = new Date(now.getFullYear(), now.getMonth(), now.getDate() - 6, 0, 0, 0, 0);
        const endToday = new Date(now.getFullYear(), now.getMonth(), now.getDate(), 23, 59, 59, 999);
        if (notifDate < start7Days || notifDate > endToday) return false;
      }

      if (filterMode === "semana_passada") {
        if (!isDateInPreviousCalendarWeek(notifDate, now)) return false;
      }

      if (filterMode === "30dias") {
        const start30Days = new Date(now.getFullYear(), now.getMonth(), now.getDate() - 29, 0, 0, 0, 0);
        const endToday = new Date(now.getFullYear(), now.getMonth(), now.getDate(), 23, 59, 59, 999);
        if (notifDate < start30Days || notifDate > endToday) return false;
      }

      if (filterMode === "period") {
        if (startDate && endDate && startDate > endDate) {
          return false;
        }
        if (startDate) {
          const start = new Date(`${startDate}T00:00:00`);
          if (notifDate < start) return false;
        }
        if (endDate) {
          const end = new Date(`${endDate}T23:59:59.999`);
          if (notifDate > end) return false;
        }
      }

      return true;
    });

    // Sempre ordena estritamente por MAIS RECENTE PRIMEIRO (createdAt DESC)
    return list.sort(
      (a, b) => normalizeNotificationTimestamp(b.createdAt) - normalizeNotificationTimestamp(a.createdAt)
    );
  }, [notifications, searchTerm, readFilter, filterMode, startDate, endDate]);

  if (!isOpen) return null;

  const handleMarkAllRead = async () => {
    await markAllNotificationsAsRead(notifications, cleanUid);
  };

  const handleClearFilters = () => {
    setSearchText("");
    setFilterMode("all");
    setReadFilter("all");
    setStartDate("");
    setEndDate("");
  };

  const formatDateTime = (createdAtVal?: any) => {
    const ts = normalizeNotificationTimestamp(createdAtVal);
    if (!ts) return "";
    try {
      const date = new Date(ts);
      if (isNaN(date.getTime())) return "";
      const dateStr = date.toLocaleDateString("pt-BR", {
        day: "2-digit",
        month: "2-digit",
        year: "numeric",
      });
      const timeStr = date.toLocaleTimeString("pt-BR", { hour: "2-digit", minute: "2-digit" });
      return `${dateStr} às ${timeStr}`;
    } catch {
      return "";
    }
  };

  const filterButtons: Array<{ id: FilterMode; label: string }> = [
    { id: "all", label: "Todas" },
    { id: "hoje", label: "Hoje" },
    { id: "ontem", label: "Ontem" },
    { id: "5dias", label: "Últimos 5 dias" },
    { id: "7dias", label: "Últimos 7 dias" },
    { id: "semana_passada", label: "Semana passada" },
    { id: "30dias", label: "Últimos 30 dias" },
    { id: "period", label: "Personalizado" },
  ];

  const modalContent = (
    <>
      {/* TELA DE HISTÓRICO DEDICADA — Ocupa a área visual inteira sem cortes, com cabeçalho fixo e rolagem própria */}
      <div className="fixed inset-0 z-[9990] bg-slate-100 dark:bg-zinc-950 flex flex-col w-screen h-screen overflow-hidden text-slate-900 dark:text-white animate-in fade-in duration-150">
        
        {/* CABEÇALHO DA TELA DE HISTÓRICO — Sempre visível no topo */}
        <header className="shrink-0 bg-white dark:bg-zinc-900 border-b border-slate-200 dark:border-zinc-800 px-4 sm:px-8 py-3.5 flex items-center justify-between shadow-sm sticky top-0 z-30">
          <div className="flex items-center gap-3 min-w-0">
            <button
              type="button"
              onClick={onClose}
              className="p-2 -ml-2 text-slate-500 hover:text-slate-900 dark:text-zinc-400 dark:hover:text-white hover:bg-slate-100 dark:hover:bg-zinc-800 rounded-xl transition cursor-pointer flex items-center gap-1 text-xs font-bold"
              title="Voltar"
            >
              <ArrowLeft size={18} />
              <span className="hidden sm:inline">Voltar</span>
            </button>
            
            <div className="p-2 bg-amber-400/10 dark:bg-amber-400/20 border border-amber-400/30 rounded-xl text-amber-600 dark:text-amber-400 shrink-0">
              <Bell size={20} />
            </div>

            <div className="truncate">
              <div className="flex items-center gap-2">
                <h1 className="font-display font-black text-base sm:text-lg text-slate-900 dark:text-white truncate">
                  🔔 Histórico de Notificações
                </h1>
                <span className="bg-slate-100 dark:bg-zinc-800 text-slate-700 dark:text-zinc-300 font-extrabold text-[11px] px-2.5 py-0.5 rounded-full border border-slate-200 dark:border-zinc-700 shrink-0">
                  {filteredNotifications.length} de {notifications.length}
                </span>
              </div>
              <p className="text-[11px] text-slate-500 dark:text-zinc-400 font-medium hidden sm:block">
                Consulte e filtre todos os avisos, confirmações e notificações recebidas
              </p>
            </div>
          </div>

          <div className="flex items-center gap-2 shrink-0">
            {notifications.some((n) => !n.read) && (
              <button
                type="button"
                onClick={handleMarkAllRead}
                className="px-3 py-1.5 bg-indigo-50 dark:bg-indigo-950/70 hover:bg-indigo-100 text-indigo-700 dark:text-indigo-300 border border-indigo-200 dark:border-indigo-800/80 rounded-xl text-xs font-bold transition flex items-center gap-1.5 cursor-pointer shadow-sm"
              >
                <CheckCheck size={14} />
                <span className="hidden md:inline">Marcar todas como lidas</span>
              </button>
            )}

            {/* Botão X Fechar — Sempre visível no canto superior direito */}
            <button
              type="button"
              onClick={onClose}
              className="flex items-center gap-1.5 px-3 py-1.5 bg-slate-100 hover:bg-slate-200 dark:bg-zinc-800 dark:hover:bg-zinc-700 text-slate-800 dark:text-white rounded-xl text-xs sm:text-sm font-bold transition cursor-pointer border border-slate-300/60 dark:border-zinc-700 shadow-sm"
              aria-label="Fechar tela de histórico"
            >
              <X size={18} />
              <span className="font-bold">Fechar</span>
            </button>
          </div>
        </header>

        {/* PAINEL DE CONTROLE FIXO — Filtros, Busca e Seletores */}
        <section className="shrink-0 bg-white/95 dark:bg-zinc-900/95 backdrop-blur-md border-b border-slate-200 dark:border-zinc-800 p-3 sm:p-5 shadow-sm">
          <div className="max-w-6xl mx-auto space-y-3">
            
            {/* Linha 1: Busca Textual + Filtro Lida/Não Lida */}
            <div className="grid grid-cols-1 md:grid-cols-12 gap-3 items-center">
              <div className="md:col-span-8 relative">
                <Search
                  size={16}
                  className="absolute left-3.5 top-1/2 -translate-y-1/2 text-slate-400"
                />
                <input
                  type="text"
                  value={searchTerm}
                  onChange={(e) => setSearchText(e.target.value)}
                  placeholder="Pesquisar em título, mensagem, tipo ou conteúdo..."
                  className="w-full pl-10 pr-9 py-2 bg-slate-50 dark:bg-zinc-950 border border-slate-200 dark:border-zinc-700 rounded-xl text-xs sm:text-sm text-slate-900 dark:text-white placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-amber-400 font-medium transition"
                />
                {searchTerm && (
                  <button
                    type="button"
                    onClick={() => setSearchText("")}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600 dark:hover:text-white p-1"
                  >
                    <X size={14} />
                  </button>
                )}
              </div>

              <div className="md:col-span-4 flex bg-slate-100 dark:bg-zinc-950 p-1 rounded-xl border border-slate-200 dark:border-zinc-800">
                <button
                  type="button"
                  onClick={() => setReadFilter("all")}
                  className={`flex-1 py-1.5 text-xs font-bold rounded-lg transition cursor-pointer ${
                    readFilter === "all"
                      ? "bg-amber-400 text-emerald-950 font-black shadow-sm"
                      : "text-slate-500 dark:text-zinc-400 hover:text-slate-900 dark:hover:text-white"
                  }`}
                >
                  Todas
                </button>
                <button
                  type="button"
                  onClick={() => setReadFilter("unread")}
                  className={`flex-1 py-1.5 text-xs font-bold rounded-lg transition cursor-pointer ${
                    readFilter === "unread"
                      ? "bg-amber-400 text-emerald-950 font-black shadow-sm"
                      : "text-slate-500 dark:text-zinc-400 hover:text-slate-900 dark:hover:text-white"
                  }`}
                >
                  Não lidas
                </button>
                <button
                  type="button"
                  onClick={() => setReadFilter("read")}
                  className={`flex-1 py-1.5 text-xs font-bold rounded-lg transition cursor-pointer ${
                    readFilter === "read"
                      ? "bg-amber-400 text-emerald-950 font-black shadow-sm"
                      : "text-slate-500 dark:text-zinc-400 hover:text-slate-900 dark:hover:text-white"
                  }`}
                >
                  Lidas
                </button>
              </div>
            </div>

            {/* Linha 2: Chips de Período Rápido */}
            <div className="flex items-center gap-1.5 overflow-x-auto pb-1 scrollbar-none">
              <span className="text-xs font-bold text-slate-400 dark:text-zinc-500 flex items-center gap-1 mr-1 shrink-0">
                <Calendar size={13} /> Período:
              </span>
              {filterButtons.map((btn) => {
                const isActive = filterMode === btn.id;
                return (
                  <button
                    key={btn.id}
                    type="button"
                    onClick={() => setFilterMode(btn.id)}
                    className={`px-3 py-1.5 text-xs font-bold rounded-xl transition whitespace-nowrap cursor-pointer shrink-0 border ${
                      isActive
                        ? "bg-emerald-700 text-white border-emerald-600 shadow-sm font-black"
                        : "bg-slate-50 dark:bg-zinc-950 text-slate-600 dark:text-zinc-400 border-slate-200 dark:border-zinc-800 hover:border-slate-300 hover:text-slate-900 dark:hover:text-white"
                    }`}
                  >
                    {btn.label}
                  </button>
                );
              })}
            </div>

            {/* Linha 3: Personalizado (Data inicial + Data final com validação) */}
            {filterMode === "period" && (
              <div className="pt-2 border-t border-slate-200/80 dark:border-zinc-800 flex flex-wrap items-center justify-between gap-3 animate-in fade-in duration-150">
                <div className="flex flex-wrap items-center gap-3">
                  <div className="flex items-center gap-1.5">
                    <span className="text-xs font-bold text-slate-500 dark:text-zinc-400">De:</span>
                    <input
                      type="date"
                      value={startDate}
                      onChange={(e) => setStartDate(e.target.value)}
                      className="px-3 py-1.5 bg-slate-50 dark:bg-zinc-950 border border-slate-200 dark:border-zinc-700 rounded-xl text-xs font-bold text-slate-900 dark:text-white focus:ring-2 focus:ring-amber-400 outline-none"
                    />
                  </div>
                  <div className="flex items-center gap-1.5">
                    <span className="text-xs font-bold text-slate-500 dark:text-zinc-400">Até:</span>
                    <input
                      type="date"
                      value={endDate}
                      onChange={(e) => setEndDate(e.target.value)}
                      className="px-3 py-1.5 bg-slate-50 dark:bg-zinc-950 border border-slate-200 dark:border-zinc-700 rounded-xl text-xs font-bold text-slate-900 dark:text-white focus:ring-2 focus:ring-amber-400 outline-none"
                    />
                  </div>

                  {isInvalidDateRange && (
                    <span className="text-xs font-bold text-rose-600 dark:text-rose-400 flex items-center gap-1 bg-rose-50 dark:bg-rose-950/60 px-2.5 py-1 rounded-lg border border-rose-200 dark:border-rose-800">
                      <ShieldAlert size={14} />
                      Data inicial deve ser menor ou igual à data final
                    </span>
                  )}
                </div>

                <button
                  type="button"
                  onClick={handleClearFilters}
                  className="text-xs text-amber-600 dark:text-amber-400 hover:underline font-bold flex items-center gap-1 cursor-pointer"
                >
                  <RotateCcw size={12} /> Limpar filtros
                </button>
              </div>
            )}
          </div>
        </section>

        {/* CONTEÚDO ROLÁVEL — Lista de Notificações com Rolagem Própria */}
        <main className="flex-1 overflow-y-auto p-4 sm:p-6 lg:p-8">
          <div className="max-w-6xl mx-auto space-y-3">
            {filteredNotifications.length === 0 ? (
              <div className="py-20 text-center space-y-3 bg-white dark:bg-zinc-900 rounded-3xl border border-slate-200 dark:border-zinc-800 p-8 shadow-sm">
                <div className="w-14 h-14 rounded-2xl bg-slate-100 dark:bg-zinc-800 flex items-center justify-center mx-auto text-slate-400">
                  <Inbox size={28} />
                </div>
                <div className="max-w-md mx-auto space-y-1">
                  <p className="text-base font-black text-slate-800 dark:text-zinc-200">
                    Nenhuma notificação encontrada no histórico.
                  </p>
                  <p className="text-xs sm:text-sm text-slate-500 dark:text-zinc-400 font-medium">
                    {searchTerm || filterMode !== "all" || readFilter !== "all"
                      ? "Nenhum resultado corresponde aos filtros e termos de busca aplicados."
                      : "Você ainda não possui avisos registrados no sistema."}
                  </p>
                </div>
                {(searchTerm || filterMode !== "all" || readFilter !== "all") && (
                  <button
                    type="button"
                    onClick={handleClearFilters}
                    className="px-4 py-2 bg-amber-400 hover:bg-amber-300 text-emerald-950 font-black rounded-xl text-xs cursor-pointer transition shadow-sm inline-flex items-center gap-1.5"
                  >
                    <RotateCcw size={14} /> Redefinir Todos os Filtros
                  </button>
                )}
              </div>
            ) : (
              filteredNotifications.map((notif) => {
                const isUnread = !notif.read;
                return (
                  <div
                    key={notif.id}
                    onClick={() => setSelectedNotification(notif)}
                    className={`p-4 sm:p-5 rounded-2xl bg-white dark:bg-zinc-900 border transition cursor-pointer flex gap-4 items-start shadow-sm hover:shadow-md ${
                      isUnread
                        ? "border-amber-400/40 bg-amber-50/20 dark:bg-amber-500/5 hover:border-amber-400/70"
                        : "border-slate-200 dark:border-zinc-800 hover:border-slate-300 dark:hover:border-zinc-700 opacity-90 hover:opacity-100"
                    }`}
                  >
                    {/* Indicador de leitura */}
                    <div className="mt-1 shrink-0">
                      {isUnread ? (
                        <span className="w-3 h-3 rounded-full bg-amber-500 block ring-4 ring-amber-400/30 animate-pulse" />
                      ) : (
                        <span className="w-3 h-3 rounded-full bg-slate-300 dark:bg-zinc-700 block" />
                      )}
                    </div>

                    <div className="flex-1 min-w-0 space-y-1.5">
                      <div className="flex flex-wrap items-center justify-between gap-2">
                        <h3 className="text-sm sm:text-base font-black text-slate-900 dark:text-white leading-snug">
                          {notif.title}
                        </h3>
                        <span className="text-xs text-slate-400 dark:text-zinc-500 font-medium whitespace-nowrap bg-slate-50 dark:bg-zinc-800/60 px-2.5 py-1 rounded-lg border border-slate-200/50 dark:border-zinc-700/50">
                          {formatDateTime(notif.createdAt)}
                        </span>
                      </div>

                      <p className="text-xs sm:text-sm text-slate-600 dark:text-zinc-300 leading-relaxed font-medium">
                        {notif.message}
                      </p>

                      {notif.linkUrl && (
                        <div className="pt-1 flex items-center gap-1 text-xs font-bold text-amber-600 dark:text-amber-400 hover:underline">
                          <span>Acessar detalhes do agendamento / conteúdo</span>
                          <ChevronRight size={14} />
                        </div>
                      )}
                    </div>
                  </div>
                );
              })
            )}
          </div>
        </main>
      </div>

      {/* MODAL DE DETALHES DA NOTIFICAÇÃO SELECIONADA */}
      <NotificationDetailModal
        notification={selectedNotification}
        onClose={() => setSelectedNotification(null)}
        onNavigate={onNavigate}
        cleanUid={cleanUid}
      />
    </>
  );

  return typeof document !== "undefined" ? createPortal(modalContent, document.body) : modalContent;
};
